import { atom, selector } from 'recoil';
import { atom_tourState } from './tour.atom';
import { atom_userAddedPlaces } from './user-added-stops.atom';
import { sideBarStateAtom } from './sidebar.atom';
import { service_tourService } from '@app/shared/services/tours.service';

export const atom_filterState = atom<string[]>({
	key: 'Filter',
	default: [],
});

export const atom_selectedFilterState = atom<string[]>({
	key: 'SelectedFilter',
	default: [],
});

// Main atom storing all bookmarks for all tours
export const atom_allBookmarksState = atom<Record<string, string[]>>({
	key: 'AllBookmarks',
	default: JSON.parse(localStorage.getItem('bookmarksByTour') || '{}'),
	effects: [
		({ onSet }) => {
			// Sync to localStorage whenever bookmarks change
			onSet((newBookmarks) => {
				localStorage.setItem('bookmarksByTour', JSON.stringify(newBookmarks));
			});
		},
	],
});

// Selector to get bookmarks for the current tour only (read-only)
export const atom_bookmarkedStopsState = selector<string[]>({
	key: 'BookmarkedStops',
	get: ({ get }) => {
		const tourState = get(atom_tourState);
		const allBookmarks = get(atom_allBookmarksState);
		const tourId = tourState?.id;

		if (!tourId) {
			return [];
		}

		return allBookmarks[tourId] || [];
	},
});

// Selector to get total saved count (bookmarked stops + user-added places) for the current tour
export const atom_totalSavedCountState = selector<number>({
	key: 'TotalSavedCount',
	get: ({ get }) => {
		const bookmarkedStops = get(atom_bookmarkedStopsState);
		const tourState = get(atom_tourState);
		const userAddedPlaces = get(atom_userAddedPlaces);
		const tourId = tourState?.id;

		const bookmarksCount = bookmarkedStops.length;
		const userAddedCount = tourId ? (userAddedPlaces[tourId]?.length || 0) : 0;

		return bookmarksCount + userAddedCount;
	},
});

// Saved-filter flag for flows where the sidebar is disabled (sidebar config 'false').
// Toggled by MapFilters' saved bubble; SavedButton continues to drive sideBarStateAtom.
export const atom_savedFilterActive = atom<boolean>({
	key: 'SavedFilterActive',
	default: false,
});

// Boolean selector: only flips when saved mode actually toggles (not on every sideBarState change).
// Sidebar-enabled tours → driven by sideBarStateAtom (set by SavedButton).
// Sidebar-disabled tours → driven by atom_savedFilterActive (set by MapFilters' saved bubble).
export const selector_isSavedMode = selector<boolean>({
	key: 'isSavedMode',
	get: ({ get }) => {
		const sidebarHidden = String(get(atom_tourState)?.config?.sidebar) === 'false';
		return sidebarHidden
			? get(atom_savedFilterActive)
			: get(sideBarStateAtom) === 'saved';
	},
});

// Derived visible stops: filters the full tour based on saved mode + bookmarks + active filters
// Only recomputes when the actual filter criteria change
export const selector_visibleStops = selector({
	key: 'visibleStops',
	get: ({ get }) => {
		const tour = get(atom_tourState);
		const isSaved = get(selector_isSavedMode);
		const bookmarks = get(atom_bookmarkedStopsState);
		const filters = get(atom_selectedFilterState);

		const allStops = tour?.stops || [];

		// Saved mode: show only bookmarked stops
		if (isSaved) {
			return allStops.filter(s => bookmarks.includes(s.id));
		}

		// Filter mode: show only stops matching selected tags
		if (filters.length > 0) {
			return allStops.filter(s =>
				s.tags?.some((t: any) => filters.includes(t.text))
			);
		}

		// Default: all stops
		return allStops;
	},
});

// Set of visible stop IDs for O(1) lookups (used by map pins)
export const selector_visibleStopIds = selector({
	key: 'visibleStopIds',
	get: ({ get }) => {
		const stops = get(selector_visibleStops);
		return new Set(stops.map(s => s.id));
	},
});

// Helper function to merge backend saved stops with localStorage saved stops
// Returns union of both sets (if bookmarked in either place, keep it)
export const mergeSavedStops = (
	backendStopIds: string[],
	localStorageStopIds: string[]
): string[] => {
	const allIds = new Set([...backendStopIds, ...localStorageStopIds]);
	return Array.from(allIds);
};

// Fetch saved stops (bookmarks) from backend and merge with localStorage
export const fetchAndMergeSavedStops = async (
	tourID: string,
	setAllBookmarks: (updater: (prev: Record<string, string[]>) => Record<string, string[]>) => void
): Promise<string[]> => {
	// Get uID from credential_items in localStorage
	const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');

	if (!uID) {
		console.warn('[fetchAndMergeSavedStops] No uID found in credential_items');
		return [];
	}

	// 1. Fetch saved stops from backend
	const backendStopIds = await service_tourService.getSavedStops(uID, tourID);

	// 2. Get current localStorage bookmarks for this tour
	const localStorageData = JSON.parse(localStorage.getItem('bookmarksByTour') || '{}');
	const localStorageStopIds: string[] = localStorageData[tourID] || [];

	// 3. Merge: union of backend and localStorage (keeps all bookmarks from both)
	const mergedStopIds = mergeSavedStops(backendStopIds, localStorageStopIds);

	// 4. Update the atom with merged bookmarks (this will also sync to localStorage via effect)
	setAllBookmarks((prev) => ({
		...prev,
		[tourID]: mergedStopIds,
	}));

	return mergedStopIds;
};